/**
 * ImportsListView — the imports list itself: toolbar, optional search chip,
 * table and pager, plus the filter/scope/search/page state that drives them.
 *
 * Extracted from `ImportsPage.tsx` on 2026-08-13 so the Search Detail page's
 * contacts tab can render the SAME list scoped to one search. The alternative —
 * a second, similar list next door — is how two screens end up disagreeing
 * about what an import is; here they cannot, because there is one component.
 *
 * The split is by OWNERSHIP of the search filter, which is the only thing the
 * two surfaces genuinely disagree about:
 *
 * - `/research/imports` keeps `search_id_thrive` in the URL, so it is
 *   deep-linkable and clearable, and passes `onClearSearchFilter` — which is
 *   also what makes the chip appear;
 * - the Search Detail tab PINS the filter to the search whose page it is. There
 *   is no chip, because a chip is a control for removing a filter, and removing
 *   this one would leave a tab called "Contact Lists" showing the firm's
 *   entire history.
 *
 * Everything else (owner scope, tag chips, free-text search, paging, the
 * download affordance) lives here and behaves identically on both surfaces.
 *
 * Design-system token utilities only; no inline `style={{}}`; English copy only.
 */
import { type ReactNode, useEffect, useRef, useState } from "react";

import { listPageSize } from "@nu-assist/shared-fe";

import type { ImportsClient } from "../../../clients/imports.client";
import type { SortState } from "../../../lib/sort";
import { ImportOwnersError } from "./ImportOwnersError";
import { ImportsPagination } from "./ImportsPagination";
import { ImportsTable } from "./ImportsTable";
import { ImportsToolbar } from "./ImportsToolbar";
import { SearchFilterChip, resolveSearchFilterLabel } from "./SearchFilterChip";
import { useImportOwners } from "./useImportOwners";
import { useImportsList } from "./useImportsList";
import { useImportsListFilters } from "./useImportsListFilters";

const SEARCH_DEBOUNCE_MS = 300;

export interface ImportsListViewProps {
  client: ImportsClient;
  /** The caller's email — drives the "Imported by" own-row accent. */
  meEmail: string;
  /** Narrow the list to one Thrive search, or `null` for the firm-wide list. */
  searchIdThrive: string | null;
  /** Display-only name for the chip, when the filter is clearable. */
  searchName?: string | null;
  /**
   * Clear the search filter. Its PRESENCE is what makes the filter clearable
   * and renders the chip — omit it to pin the filter (the Search Detail tab).
   */
  onClearSearchFilter?: () => void;
  /**
   * Apply (or replace) the search filter from the toolbar's searches typeahead.
   * Like `onClearSearchFilter`, its PRESENCE is the switch: omitting it is how
   * the Search Detail tab keeps its filter pinned to the search whose page it
   * is. The two travel together — a surface that cannot clear the filter must
   * not be able to replace it either.
   */
  onApplySearchFilter?: (searchId: string, searchName: string | null) => void;
  /** `you` | `all` | an email. The imports page starts at `you` unless it
   *  arrived with a search filter; the Search Detail tab always starts at
   *  `all`. */
  initialOwner: string;
  /** Toolbar header slot (the imports page's "+ New Import" CTA). */
  headerAction?: ReactNode;
  /** Rendered between the chip and the table (one-shot notices). */
  notice?: ReactNode;
  /** Toolbar title. Defaults to "Contact Lists"; the Search Detail tab passes
   *  the same, so the panel and the tab that opened it read alike. */
  title?: string;
  /** Semantic level for the toolbar's title — `h2` where the page already
   *  owns its `h1`. */
  headingLevel?: "h1" | "h2";
  /**
   * Keep the scope / chips / search / page in the URL, so the reader can open a
   * contact list and come back to the list they were reading.
   *
   * OPT-IN, and the default is today's behaviour. Search Detail's People Lists
   * tab must NOT pass it: that page unmounts its inactive tabs on purpose and
   * persists the TAB rather than the reading position inside it
   * (`SearchDetailPage.tsx`, pinned by its own test). See
   * `useImportsListFilters` for why the persisted setters are named for intent.
   */
  urlFilters?: boolean;
}

export function ImportsListView({
  client,
  meEmail,
  searchIdThrive,
  searchName,
  onClearSearchFilter,
  onApplySearchFilter,
  initialOwner,
  headerAction,
  notice,
  title,
  headingLevel,
  urlFilters = false,
}: ImportsListViewProps) {
  // Rows per page, resolved per stage from `@nu-assist/shared-fe` (local 20 /
  // dev 50 / prod 100) so this list moves in lockstep with Searches and the
  // contacts table instead of carrying its own literal. Read once here and used
  // for the query, the page-count math and the pager, so those three can never
  // disagree.
  const pageSize = listPageSize();

  // The scope, the chips (multi-select, OR semantics — empty is "All"), the raw
  // input, the debounced term and the page. Backed by component state or by the
  // URL depending on `urlFilters`; the operations below are identical either
  // way, which is the point of the hook.
  const {
    owner,
    tags,
    searchText,
    q,
    page,
    setSearchText,
    commitQ,
    setOwner,
    toggleTag,
    goToPage,
    onSearchFilterChanged,
  } = useImportsListFilters({ persist: urlFilters, initialOwner });
  const [sort, setSort] = useState<SortState>({ key: "created_at", dir: "desc" });

  // Debounce the search input into `q`; `commitQ` resets to page 1 with it, in
  // ONE write. The guard is CONDITIONAL because this effect also fires once on
  // mount: an unconditional commit would fire ~300ms in and undo a page the
  // reader clicked inside that window — and in URL mode it would also delete a
  // `?page=` they arrived with, which is the whole feature.
  useEffect(() => {
    const id = setTimeout(() => {
      // `.trim()` on BOTH sides, so the comparison is like with like. The draft
      // is raw and the committed term is trimmed, so without it a padded input
      // can never converge: the guard fails forever, a second timer commits
      // again ~300ms later, and that commit resets the page — silently eating a
      // pager click the reader made inside the window. Which is the exact
      // hazard this guard exists to prevent, failing open for anyone who typed
      // a trailing space.
      if (searchText.trim() === q) return;
      commitQ(searchText);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
    // `q` is in the deps so the guard above can compare against the CURRENT
    // committed search. Cost: when `q` settles, this re-runs and schedules one
    // more timer that immediately early-returns. No loop — `q` cannot change
    // again without `searchText` moving first. Do not "clean up" the dep.
  }, [searchText, q, commitQ]);

  // The search filter can change under a mounted view (a second My Searches
  // CTA, a back/forward, a route change between two searches). It narrows the
  // list like any other filter and has to reset the page like one — the
  // in-component filters do it in their handlers, but this one has no handler
  // to hang it on.
  //
  // ORDERING NOTE — this effect is declared ABOVE the clamp further down, and
  // the two can both fire on the same render (the filter changes while the
  // caller is already past the end). React runs them in declaration order and
  // batches both `setPage` calls, so the clamp wins and the caller lands on the
  // last page of the NEW filter rather than on page 1. Narrow, self-healing on
  // the next fetch, and left as-is — but the outcome depends on the order these
  // two blocks appear in the file, which is invisible. Do not reorder them
  // during a tidy-up without deciding which should win.
  //
  // It also widens `you` → `all` ON ARRIVAL, and that is not a courtesy: the
  // imports for a search are usually somebody else's (a search has a team), so
  // a reader scoped to Me who applies a search from the toolbar lands on an
  // empty list next to a chip claiming to filter — the worst of both readings.
  // `ImportsPage` already reaches this conclusion for the DEEP-LINK case
  // through `initialOwner`; the toolbar filter is the same situation arriving
  // later, and `ImportPicker` widens on the same param for the same reason.
  //
  // ARRIVAL, not every change. `prevSearchIdRef` is the whole distinction:
  // no filter → a filter is an arrival and widens; one search REPLACING another
  // does not. (Arrival can no longer be observed on the MOUNT pass — see the
  // sentinel note below — so this is about changes under a mounted view.) The replacement case is a different situation entirely — by then
  // the reader has already seen the widening, and if they went back to Me after
  // it they meant it. Re-widening on the next search silently overrides a
  // choice made AFTER the argument for widening had already been made once.
  // (Applying search A, picking Me, then switching to search B used to land the
  // reader back on Everyone with nothing on screen to explain it.)
  //
  // Only `you` is widened — an explicit recruiter scope is a choice, and
  // replacing it would drop the rows they asked for. The widening therefore
  // cannot become a second, competing source of truth for `initialOwner`.
  //
  // (This block used to end by explaining that the ref starts at `null` "so
  // mounting WITH a filter still counts as an arrival". That rationale is gone
  // with the mount guard below: the mount pass now writes nothing at all, so
  // arrival is only ever observed for a change under a mounted view.)
  //
  // Deliberately one-directional. Clearing the filter does NOT snap the scope
  // back, matching `ImportsPage.clearSearchFilter`: the reader can see it says
  // Everyone and change it, whereas silently narrowing would delete rows they
  // were looking at. It does reset the ref, so a search applied AFTER a clear
  // is an arrival again.
  //
  // `ImportPicker` diverges on BOTH halves — it un-widens on clear, and it
  // widens on replacement as well as on arrival. The first is deliberate: its
  // finder is a transient overlay whose scope has no on-screen life after it
  // closes, so restoring `you` costs the recruiter nothing. The second is
  // simply the behaviour this component had until 2026-08-14 and has not been
  // revisited there. `ImportPicker.tsx` carries the matching note; if it is
  // ever brought in line, the two effects should be read side by side rather
  // than one copied onto the other, because only the un-widening is shared.
  //
  // Deps are exactly `[searchIdThrive]` — the filter VALUE, which is the whole
  // decision. `page` and `owner` are what the effect RESETS, not inputs to it,
  // the ref is a ref, and the functional `setOwner` update reads the current
  // scope at apply time rather than capturing it, so nothing here can go stale.
  // Biome agrees (both setters are stable), which is why the
  // `useExhaustiveDependencies` suppression this block used to carry is gone
  // rather than merely quiet.
  //
  // SKIPPED ON THE MOUNT PASS, and that is not a tidy-up. Against component
  // state the mount run was a harmless no-op — `page` already starts at 1, and
  // both hosts seed `all` when a filter is present, so the widening had nothing
  // to do. Against the URL it is destructive on both halves: arriving at
  // `?page=3` would delete the page, and arriving at `?owner=you` beside a
  // search filter would be widened back to `all` — overriding a scope the
  // reader chose AFTER the widening had already had its say, which is exactly
  // what the replacement case above argues against.
  //
  // ONE ref, compared BY VALUE, and the shape is chosen for StrictMode rather
  // than merely surviving it. The first version used a separate boolean
  // first-run flag, which is not double-invoke-safe: React runs
  // mount → cleanup → mount in dev (`main.tsx` wraps the app in
  // `<StrictMode>`), refs persist across that simulated remount, so the second
  // pass saw the flag already false and fell through. It called
  // `onSearchFilterChanged(false)` — NOT a no-op, because a filter write resets
  // the page — and deleted the `?page=` the reader had just arrived with. In
  // every developer's local environment, and in any Playwright run against a
  // dev server.
  //
  // The `undefined` sentinel is what distinguishes "never seen" from "seen, and
  // it was null". Both guards below are load-bearing: the first swallows the
  // mount pass, the second swallows the StrictMode re-invoke AND any re-render
  // that did not actually change the filter.
  // The sentinel is BOXED rather than `undefined`. `undefined` reads naturally
  // but lives in the prop's own domain: the day `searchIdThrive` is made
  // optional, or a host passes `data?.id_thrive`, `prev === undefined` is true
  // on every run and the effect silently stops firing — no type error, and no
  // test that names the cause. A box makes "never seen" unrepresentable as a
  // filter value, so that failure cannot be written.
  const prevSearchIdRef = useRef<{ value: string | null } | null>(null);
  useEffect(() => {
    const current = searchIdThrive ?? null;
    const prev = prevSearchIdRef.current;
    prevSearchIdRef.current = { value: current };
    if (prev === null) return; // mount pass — never writes
    if (prev.value === current) return; // StrictMode re-invoke / no real change
    // ARRIVAL is "there was no filter, and now there is". Computed from `prev`
    // rather than from a ref read after the write, so the two guards above
    // cannot make it lie.
    onSearchFilterChanged(!prev.value && !!current);
  }, [searchIdThrive, onSearchFilterChanged]);

  const query = useImportsList({
    client,
    owner,
    tag: tags,
    // A combined list carries `['raw','combined']` tags, so the server's
    // `tags && ['raw']` overlap returns it under the "Raw" chip while it is
    // labelled "Combined" (/pr-review iter 1, Agent C M5). Drop it server-side
    // unless the Combined chip is on.
    excludeTag: tags.includes("raw") && !tags.includes("combined") ? "combined" : null,
    q,
    searchIdThrive,
    page,
    pageSize,
    sortKey: sort?.key,
    sortDir: sort?.dir,
  });

  const items = query.data?.items ?? [];
  const total = query.data?.total ?? 0;

  // Pull the caller back onto a page that exists when the total shrinks under
  // them. Every in-component filter resets the page itself, but the list also
  // refetches on a 3s interval while any row is still parsing or exporting, so
  // `total` can drop with no user action at all — leaving an empty table on a
  // page past the end. Gated on `isSuccess` so the placeholder 0 during the
  // first load (and after an error) never yanks the page.
  //
  // `!isFetching` is the guard, and it has to be that rather than the narrower
  // `!isPlaceholderData`: only clamp on a total that has actually settled.
  //
  // `placeholderData: (prev) => prev` covers a key with NO cached data. A key
  // that HAS cached data returns it as real — `isPlaceholderData` false,
  // `isSuccess` true, `isFetching` true while the background refetch runs — and
  // with no `staleTime` anywhere, every cache hit takes that path. So a
  // placeholder-only guard still clamps on a stale total, and the poisoned
  // entry is one the clamp itself causes: page 7 shrinks, we fetch and cache
  // `{items: [], total: 12, page: 7}`, clamp to 1; the total later recovers,
  // the caller clicks 7, the cache answers 12 before the refetch lands, and
  // their click is silently eaten. `isFetching` covers both — it is true during
  // the placeholder window AND during a stale-cache revalidation — and the 3s
  // poll only runs while rows are unsettled, so it never starves the clamp.
  //
  // `goToPage`, never a filter setter: the clamp moves the page and must not
  // reset it to 1. It is also the only URL write that can fire with no user
  // action, and it goes out as `replace` like every other one — a pushed clamp
  // would create a history entry for a page that does not exist, so Back would
  // land there, re-clamp, and trap the reader.
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  useEffect(() => {
    if (query.isSuccess && !query.isFetching && page > pageCount) goToPage(pageCount);
  }, [query.isSuccess, query.isFetching, page, pageCount, goToPage]);

  // The per-recruiter menu options come from their own endpoint, so they stay
  // the same set whatever the tag chip / search / page / scope is doing. The
  // previous `deriveOwners(items)` read them off the visible page, which meant
  // filtering the list quietly filtered the control that drives it.
  const { query: ownersQuery, owners } = useImportOwners({ client });

  // The toolbar reports WHICH chip was clicked and WHICH scope was picked; the
  // toggle arithmetic and the page reset both moved into `useImportsListFilters`
  // so they stay one write. `changeOwner` / `changeTag` used to live here purely
  // to pair a setter with `setPage(1)`, which is now the setter's own job.

  function handleSort(key: string) {
    setSort((prev) =>
      prev?.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }
    );
    goToPage(1);
  }

  // Download the import's original file: resolve a presigned GET, then trigger a
  // browser download via a transient anchor (no new tab, keeps the filename).
  async function handleDownload(importId: string, filename: string) {
    try {
      const { presigned_get_url } = await client.getDownloadLink(importId);
      const a = document.createElement("a");
      a.href = presigned_get_url;
      a.download = filename;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch {
      // Best-effort UX affordance; a failed presign is non-fatal to the list.
    }
  }

  return (
    <>
      <ImportsToolbar
        total={total}
        searchText={searchText}
        onSearchTextChange={setSearchText}
        activeTags={tags}
        onTagsChange={toggleTag}
        owner={owner}
        meEmail={meEmail}
        owners={owners}
        onOwnerChange={setOwner}
        headerAction={headerAction}
        title={title}
        headingLevel={headingLevel}
        // The three parts of the searches typeahead travel as one object, so
        // the surface that pins its filter opts out of all of them at once
        // rather than passing a callback the toolbar would silently ignore.
        searchFilter={
          onApplySearchFilter
            ? {
                client,
                appliedSearchId: searchIdThrive,
                onApply: onApplySearchFilter,
              }
            : undefined
        }
      />

      {searchIdThrive && onClearSearchFilter ? (
        <SearchFilterChip
          label={resolveSearchFilterLabel({
            searchId: searchIdThrive,
            nameFromUrl: searchName ?? null,
            items,
          })}
          onClear={onClearSearchFilter}
        />
      ) : null}

      {notice}
      <ImportOwnersError query={ownersQuery} variant="banner" />

      {query.isError ? (
        <div
          data-testid="imports-error"
          className="rounded-md border border-error-strong bg-error-muted px-4 py-3 text-body text-error"
        >
          Couldn’t load people lists. Please try again.
        </div>
      ) : (
        <>
          <ImportsTable items={items} meEmail={meEmail} onDownload={handleDownload} sort={sort} onSort={handleSort} />
          <ImportsPagination
            total={total}
            page={page}
            pageSize={pageSize}
            onPageChange={goToPage}
          />
        </>
      )}
    </>
  );
}
